import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  CreditCard,
  CheckCircle,
  Cancel,
  ChevronLeft
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { quikqAuthService } from '../services/quikqAuth';
import { quikqApiService, CardInfo } from '../services/quikqApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cards-tabpanel-${index}`}
      aria-labelledby={`cards-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Cards: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cards, setCards] = useState<CardInfo[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0); // 0 = Active, 1 = Inactive

  // Get driver name by asset ID
  const getDriverName = (assetId: string) => {
    if (!assetId || !drivers.length) return 'N/A';
    
    const driver = drivers.find(d => d.driverId === assetId);
    if (driver) {
      return `${driver.firstName} ${driver.lastName}`;
    }
    
    // If not found in drivers, it might be a unit ID
    return `Unit ${assetId}`;
  };

  // Format card number without dashes
  const formatCardNumber = (cardNo: string) => {
    if (!cardNo) return 'N/A';
    
    // Remove any non-digit characters and return clean number
    return cardNo.replace(/\D/g, '');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Fetch drivers data
  const fetchDrivers = async () => {
    try {
      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser?.startCode) {
        return;
      }

      const driversData = await quikqApiService.getCarrierDrivers(currentUser.startCode);
      // console.log('🔍 Fetched drivers:', driversData);
      setDrivers(driversData || []);
    } catch (err) {
      // console.log('⚠️ Could not fetch drivers:', err);
      setDrivers([]);
    }
  };

  // Fetch cards data
  const fetchCards = async () => {
    setLoading(true);
    setError('');
    
    try {
      const currentUser = quikqAuthService.getCurrentUser();
      if (!currentUser?.startCode) {
        throw new Error('No authenticated user found');
      }

      const cardsData = await quikqApiService.getCarrierCards(currentUser.startCode);
      // console.log('🔍 Fetched cards:', cardsData);
      setCards(cardsData || []);
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError('Error loading cards. Please try again.');
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
    fetchDrivers();
  }, []);

  // Filter cards based on active tab
  const activeCards = cards.filter(card => card.status === 'Active');
  const inactiveCards = cards.filter(card => card.status === 'Inactive');
  
  // console.log('🔍 Total cards:', cards.length);
  // console.log('🔍 Active cards:', activeCards.length);
  // console.log('🔍 Inactive cards:', inactiveCards.length);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, pb: { xs: 12, sm: 12, md: 12 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, pb: { xs: 12, sm: 12, md: 12 } }}>
      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          position: 'relative',
          zIndex: 1
        }}>
          <Box
            onClick={() => navigate('/profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'primary.main',
              mr: 2,
              '&:hover': { color: 'primary.dark' }
            }}
          >
            <ChevronLeft sx={{ fontSize: 20, mr: 0.5 }} />
            <Typography variant="body2" fontWeight="500">Back</Typography>
          </Box>
        </Box>

        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ position: 'relative', zIndex: 1 }}>
          Cards Information
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="cards tabs">
          <Tab 
            label={`Active Cards (${activeCards.length})`} 
            id="cards-tab-0"
            aria-controls="cards-tabpanel-0"
          />
          <Tab 
            label={`Inactive Cards (${inactiveCards.length})`} 
            id="cards-tab-1"
            aria-controls="cards-tabpanel-1"
          />
        </Tabs>
      </Paper>

      {/* Active Cards Tab */}
      <TabPanel value={tabValue} index={0}>
        {activeCards.length > 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2 
          }}>
                        {activeCards.map((card, index) => {
              // console.log('🔍 Rendering active card:', card);
              return (
                <Card key={index} elevation={1} sx={{ 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.main'
                  }
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 2
                    }}>
                      {/* Left side - Card info */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: 1,
                        flex: 1
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          mb: 1
                        }}>
                          <CreditCard sx={{ color: 'primary.main', fontSize: 20 }} />
                                                     <Typography variant="h6" fontWeight={600}>
                             Card: {formatCardNumber(card.cardNo)}
                           </Typography>
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: 1,
                          alignItems: 'flex-start'
                        }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1 
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              Type:
                            </Typography>
                            <Chip 
                              label={card.cardType || 'N/A'} 
                              size="small"
                              color={card.cardType === 'Driver' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </Box>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1 
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              Asset ID:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {card.assetId || 'N/A'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1 
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              Using:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {getDriverName(card.assetId)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Right side - Status */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        alignSelf: { xs: 'flex-start', sm: 'center' }
                      }}>
                        <Chip 
                          icon={<CheckCircle />}
                          label={card.status}
                          size="medium"
                          color="success"
                          sx={{ 
                            fontWeight: 600,
                            px: 2,
                            py: 1
                          }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Active Cards
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You don't have any active cards at the moment.
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Inactive Cards Tab */}
      <TabPanel value={tabValue} index={1}>
        {inactiveCards.length > 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2 
          }}>
            {inactiveCards.map((card, index) => {
              // console.log('🔍 Rendering inactive card:', card);
              return (
              <Card key={index} elevation={1} sx={{ 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                opacity: 0.8,
                '&:hover': {
                  boxShadow: 3,
                  borderColor: 'text.secondary'
                }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2
                  }}>
                    {/* Left side - Card info */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 1,
                      flex: 1
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        mb: 1
                      }}>
                        <CreditCard sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                 <Typography variant="h6" fontWeight={600} color="text.secondary">
                           Card: {formatCardNumber(card.cardNo)}
                         </Typography>
                      </Box>
                      
                                             <Box sx={{ 
                         display: 'flex', 
                         flexDirection: 'column',
                         gap: 1,
                         alignItems: 'flex-start'
                       }}>
                         <Box sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           gap: 1 
                         }}>
                           <Typography variant="body2" color="text.secondary">
                             Type:
                           </Typography>
                           <Chip 
                             label={card.cardType || 'N/A'}
                             size="small"
                             color="default"
                             variant="outlined"
                           />
                         </Box>
                         
                         <Box sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           gap: 1 
                         }}>
                           <Typography variant="body2" color="text.secondary">
                             Asset ID:
                           </Typography>
                           <Typography variant="body2" color="text.secondary">
                             {card.assetId || 'N/A'}
                           </Typography>
                         </Box>
                         
                         <Box sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           gap: 1 
                         }}>
                           <Typography variant="body2" color="text.secondary">
                             Using:
                           </Typography>
                           <Typography variant="body2" color="text.secondary">
                             {getDriverName(card.assetId)}
                           </Typography>
                         </Box>
                       </Box>
                    </Box>

                    {/* Right side - Status */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      alignSelf: { xs: 'flex-start', sm: 'center' }
                    }}>
                      <Chip 
                        icon={<Cancel />}
                        label={card.status}
                        size="medium"
                        color="default"
                        sx={{ 
                          fontWeight: 600,
                          px: 2,
                          py: 1
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
            })}
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Inactive Cards
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All your cards are currently active.
            </Typography>
          </Paper>
        )}
      </TabPanel>
    </Container>
  );
};

export default Cards;
